/**
 * Subscription Admin Repository
 * Super Admin용 구독 관리 데이터 액세스 계층
 */

import axios from 'axios';
import { API_BASE_URL } from '../config/api';

export interface OrganizationSubscription {
  // Organization 기본 정보
  id: string;
  name: string;
  owner_name: string;
  phone: string;
  email: string;
  business_type: string;
  registration_status: string;

  // 구독 정보
  subscription_plan: 'FREE' | 'STARTER' | 'BASIC' | 'PRO' | 'PREMIUM' | 'ENTERPRISE' | null;
  subscription_status: 'trial' | 'active' | 'expired' | 'suspended' | null;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  plan_started_at: string | null;
  plan_ends_at: string | null;
  discount_rate?: number;  // 할인율

  // 요금 정보 (owner_subscriptions에서 가져옴)
  monthly_fee?: number;      // 월 요금 (paid_amount / contract_months)
  paid_amount?: number;      // 총 결제금액
  contract_months?: number;  // 계약 개월수
  custom_alimtalk_free_quota?: number;  // 커스텀 알림톡 무료 제공량

  // 배포 모드
  deploy_mode?: 'local_ubuntu' | 'local_windows' | null;

  // 메타
  created_at: string;
  updated_at: string;
  approved_at: string | null;
}

// 구독 알림 통계
export interface SubscriptionAlertStats {
  total: number;
  by_status: {
    trial: number;
    active: number;
    suspended: number;
    expired: number;
  };
  by_plan: {
    FREE: number;
    STARTER: number;
    BASIC: number;
    PRO: number;
    PREMIUM: number;
  };
  alerts: {
    expiring_7days: number;
    payment_pending: number;
    unpaid: number;
  };
}

// 만료 임박 업체
export interface ExpiringOrganization {
  id: string;
  name: string;
  plan: string | null;
  status: string | null;
  ends_at: string | null;
  days_remaining: number | null;
  owner_name: string | null;
  owner_email: string | null;
  owner_phone: string | null;
  last_notification: {
    type: string;
    sent_at: string;
    channel: string;
    status: string;
  } | null;
}

// 미결제 업체
export interface UnpaidOrganization {
  id: string;
  name: string;
  type: 'trial_expired' | 'plan_expired' | 'payment_pending';
  plan: string | null;
  status: string | null;
  expired_at: string | null;
  days_overdue: number | null;
  owner_name: string | null;
  owner_email: string | null;
  owner_phone: string | null;
  pending_request: {
    id: string;
    requested_plan: string;
    requested_at: string;
    depositor_name: string | null;
    deposit_amount: number | null;
    deposit_notified_at: string | null;
  } | null;
}

// 결제 대기 플랜 신청
export interface PendingPlanRequest {
  id: string;
  organization_id: string;
  organization_name: string | null;
  owner_name: string | null;
  owner_email: string | null;
  owner_phone: string | null;
  requested_plan: string;
  requested_at: string;
  depositor_name: string | null;
  deposit_amount: number | null;
  deposit_notified_at: string | null;
  status: string;
}

// 플랜 신청 승인 요청
export interface ConfirmPlanRequestBody {
  start_date?: string;
  months: number;
  notes?: string;
}

// 플랜 신청 거부 요청
export interface RejectPlanRequestBody {
  reason: string;
}

export interface UpdatePlanRequest {
  plan: 'starter' | 'basic' | 'pro' | 'premium';
  months: number;
  start_date: string;      // 시작일 (YYYY-MM-DD) - 매월 결제일 기준
  discount_rate?: number;  // 할인율 (0-100%)
  paid_amount?: number;    // 직접 입력한 결제 금액
  custom_alimtalk_free_quota?: number;  // 커스텀 알림톡 무료 제공량
  billing_type?: 'prepaid' | 'postpaid';  // 선불/후불
}

export interface CurrentSettings {
  plan_name: string;
  plan_display_name: string;
  months: number;
  start_date: string;
  end_date: string;
  original_price: number;
  paid_amount: number;
  monthly_fee: number;
  discount_rate: number;
  custom_alimtalk_free_quota: number | null;
  default_alimtalk_free_quota: number;
  billing_type?: 'prepaid' | 'postpaid';
}

export interface SubscriptionDetail {
  organization_id: string;
  organization_name: string;
  discount_rate?: number;
  subscription: {
    plan: string;
    status: string;
    is_active: boolean;
    ends_at: string | null;
    days_remaining: number | null;
    needs_renewal: boolean;
    limits: {
      max_users: number;
      max_gates: number;
      max_branches: number;
      price_per_month: number | null;
    };
  };
  current_settings?: CurrentSettings;
}

export class SubscriptionAdminRepository {
  private baseUrl = `${API_BASE_URL}/api/v1/admin/owner-approvals`;

  private getAuthHeaders() {
    // Admin 로그인은 admin_token 사용
    const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * 전체 조직 목록 조회 (구독 정보 포함)
   */
  async getAllOrganizations(): Promise<OrganizationSubscription[]> {
    const response = await axios.get(`${this.baseUrl}/all`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  /**
   * 플랜 업데이트 (기간제 라이선스)
   */
  async updatePlan(
    organizationId: string,
    request: UpdatePlanRequest
  ): Promise<any> {
    const response = await axios.post(
      `${this.baseUrl}/${organizationId}/plan`,
      request,
      {
        headers: this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  /**
   * 구독 상세 정보 조회
   */
  async getSubscription(organizationId: string): Promise<SubscriptionDetail> {
    const response = await axios.get(
      `${this.baseUrl}/${organizationId}/subscription`,
      {
        headers: this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  // ============================================================
  // 구독 알림/미결제 관리 API (subscription-alerts)
  // ============================================================

  private alertsBaseUrl = `${API_BASE_URL}/api/v1/admin/subscription-alerts`;

  /**
   * 구독 통계 조회
   */
  async getAlertStats(): Promise<SubscriptionAlertStats> {
    const response = await axios.get(`${this.alertsBaseUrl}/stats`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  /**
   * 만료 임박 업체 목록 조회
   */
  async getExpiringOrganizations(days: number = 7): Promise<{
    organizations: ExpiringOrganization[];
    total: number;
  }> {
    const response = await axios.get(`${this.alertsBaseUrl}/expiring`, {
      params: { days },
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  /**
   * 미결제 업체 목록 조회
   */
  async getUnpaidOrganizations(): Promise<{
    organizations: UnpaidOrganization[];
    total: number;
  }> {
    const response = await axios.get(`${this.alertsBaseUrl}/unpaid`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  /**
   * 결제 대기 플랜 신청 목록 조회
   */
  async getPendingRequests(): Promise<{
    requests: PendingPlanRequest[];
    total: number;
  }> {
    const response = await axios.get(`${this.alertsBaseUrl}/pending-requests`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  /**
   * 플랜 신청 승인
   */
  async confirmPlanRequest(
    requestId: string,
    body: ConfirmPlanRequestBody
  ): Promise<any> {
    const response = await axios.post(
      `${this.alertsBaseUrl}/requests/${requestId}/confirm`,
      body,
      {
        headers: this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  /**
   * 플랜 신청 거부
   */
  async rejectPlanRequest(
    requestId: string,
    body: RejectPlanRequestBody
  ): Promise<any> {
    const response = await axios.post(
      `${this.alertsBaseUrl}/requests/${requestId}/reject`,
      body,
      {
        headers: this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  /**
   * 알림 발송
   */
  async sendNotification(
    orgId: string,
    type: 'expiry_warning' | 'payment_reminder' | 'suspension_notice',
    channel: 'email' | 'alimtalk' = 'email'
  ): Promise<any> {
    const response = await axios.post(
      `${this.alertsBaseUrl}/${orgId}/send-notification`,
      { type, channel },
      {
        headers: this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  /**
   * 서비스 정지
   */
  async suspendOrganization(orgId: string, reason: string = '미결제'): Promise<any> {
    const response = await axios.post(
      `${this.alertsBaseUrl}/${orgId}/suspend`,
      null,
      {
        params: { reason },
        headers: this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  /**
   * 서비스 재활성화
   */
  async reactivateOrganization(
    orgId: string,
    plan: string,
    months: number = 1
  ): Promise<any> {
    const response = await axios.post(
      `${this.alertsBaseUrl}/${orgId}/reactivate`,
      null,
      {
        params: { plan, months },
        headers: this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  // ============================================================
  // 동기화 API 키 관리
  // ============================================================

  /**
   * 동기화 API 키 조회
   */
  async getSyncKey(orgId: string): Promise<{
    organization_id: string;
    organization_name: string;
    sync_api_key: string | null;
    has_key: boolean;
  }> {
    const response = await axios.get(
      `${this.baseUrl}/${orgId}/sync-key`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * 동기화 API 키 발급/재발급
   */
  async generateSyncKey(orgId: string): Promise<{
    success: boolean;
    sync_api_key: string;
    message: string;
  }> {
    const response = await axios.post(
      `${this.baseUrl}/${orgId}/sync-key/generate`,
      null,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * 동기화 API 키 폐기
   */
  async revokeSyncKey(orgId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await axios.delete(
      `${this.baseUrl}/${orgId}/sync-key`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * 로컬 서버 URL 설정
   */
  async setLocalServerUrl(orgId: string, localServerUrl: string): Promise<any> {
    const response = await axios.put(
      `${this.baseUrl}/${orgId}/local-server-url`,
      { local_server_url: localServerUrl },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * 배포 모드 변경 (local_ubuntu / local_windows)
   */
  async setDeployMode(orgId: string, deployMode: string): Promise<any> {
    const response = await axios.put(
      `${this.baseUrl}/${orgId}/deploy-mode`,
      { deploy_mode: deployMode },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * 알림톡 로그 동기화 풀 (로컬 → NTD)
   */
  async pullNotificationLogs(orgId: string): Promise<{
    success: boolean;
    message: string;
    detail?: any;
  }> {
    const response = await axios.post(
      `${this.baseUrl}/${orgId}/sync-pull-notification-logs`,
      null,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
}
